import React, { useState, useMemo, useEffect } from "react";
import moment from "moment";
import { 
  FiSearch, FiEye, FiChevronLeft, FiChevronRight, 
  FiGrid, FiUser, FiClock, FiCalendar 
} from "react-icons/fi";

export default function TimesheetTable({ timesheets = [], onViewTimesheet, loading = false, isApprover = true }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const handleViewClick = (id) => {
    if (onViewTimesheet) onViewTimesheet(id);
  };

  const flattenedData = useMemo(() => {
    return [...timesheets]
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
      .flatMap((ts) => {
        if (!ts.entries) return [];
        return ts.entries.map((entry) => ({
          _id: ts._id, 
          name: ts.name || "Unknown Staff",
          department: ts.department || "N/A",
          date: moment(entry.date).format("YYYY-MM-DD"),
          submittedAt: ts.createdAt, 
          activity: entry.activity || "No Activity",
          project: entry.project || "-",
          status: ts.status || "pending",
          entryHours: entry.hours || 0,
        }));
      });
  }, [timesheets]);

  const filteredData = useMemo(() => {
    const q = search.toLowerCase();
    return flattenedData.filter(item => {
      const matchesSearch =
        item.name.toLowerCase().includes(q) || 
        item.project.toLowerCase().includes(q) ||
        item.activity.toLowerCase().includes(q);

      const matchesStatus =
        statusFilter === "all" || item.status.toLowerCase() === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [flattenedData, search, statusFilter]);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(start, start + itemsPerPage);
  }, [filteredData, currentPage]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "approved": return "bg-green-100 text-green-800 border-green-200";
      case "rejected": return "bg-red-100 text-red-800 border-red-200";
      default: return "bg-orange-100 text-orange-800 border-orange-200";
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-xl p-12 flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mb-4"></div>
        <p className="text-gray-500 font-medium">Loading timesheets...</p>
      </div>
    );
  }

  return (
    <div className="bg-transparent md:bg-white rounded-xl overflow-hidden">

      {/* Search + Status Filter Header */}
      <div className="p-4 bg-white md:bg-gray-50 border-b border-gray-100 md:border-gray-200 rounded-2xl md:rounded-none mb-4 md:mb-0">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          {/* Search */}
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={isApprover ? "Search staff or projects..." : "Search my activities..."}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-orange-500 outline-none text-sm"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="py-3 px-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-orange-500 outline-none text-sm font-bold text-gray-600 bg-white"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>

          {/* Record count */}
          <div className="text-xs font-bold text-gray-400 uppercase tracking-widest whitespace-nowrap self-center">
            {filteredData.length} record{filteredData.length !== 1 ? "s" : ""}
          </div>
        </div>
      </div>

      {/* Empty state */}
      {filteredData.length === 0 && (
        <div className="bg-white py-16 flex flex-col items-center justify-center text-center px-4">
          <div className="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center mb-4">
            <FiGrid className="text-orange-300" size={32} />
          </div>
          <p className="text-gray-700 font-bold text-lg mb-1">No timesheets found</p>
          <p className="text-gray-400 text-sm">
            {search || statusFilter !== "all"
              ? "Try adjusting your search or filter"
              : "No timesheets have been submitted yet"}
          </p>
        </div>
      )}

      {/* Desktop View */}
      {paginatedData.length > 0 && (
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {isApprover && (
                  <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    Staff Member
                  </th>
                )}
                {["Log Date", "Submitted", "Project", "Activity", "Hours", "Status", "Action"].map((h) => (
                  <th key={h} className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {paginatedData.map((item, idx) => (
                <tr key={idx} className="hover:bg-orange-50/20 transition-colors">
                  {isApprover && (
                    <td className="px-6 py-4">
                      <div className="font-bold text-gray-800 text-sm">{item.name}</div>
                      <div className="text-[10px] text-gray-400 uppercase font-bold">{item.department}</div>
                    </td>
                  )}
                  <td className="px-6 py-4 text-sm text-gray-600">{item.date}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-[11px] font-bold text-gray-700">
                      {moment(item.submittedAt).format("DD MMM YYYY")}
                    </div>
                    <div className="text-[9px] text-orange-500 font-black uppercase tracking-tighter">
                      {moment(item.submittedAt).format("hh:mm A")}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-bold text-orange-600">{item.project}</td>
                  <td className="px-6 py-4 text-sm text-gray-700 truncate max-w-[180px]">{item.activity}</td>
                  <td className="px-6 py-4 text-sm font-black">{item.entryHours}h</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-[9px] font-black uppercase border ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleViewClick(item._id)}
                      className="p-2 text-orange-600 hover:bg-orange-100 rounded-lg transition-colors"
                    >
                      <FiEye size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile Card View */}
      {paginatedData.length > 0 && (
        <div className="md:hidden space-y-4 px-1">
          {paginatedData.map((item, idx) => (
            <div key={idx} className="bg-white p-5 rounded-3xl border border-gray-100 shadow-sm">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-50 rounded-2xl flex items-center justify-center text-orange-600">
                    <FiClock size={20} />
                  </div>
                  <div>
                    <h4 className="font-black text-gray-800 text-sm">{item.date}</h4>
                    <p className="text-[10px] text-gray-400 font-bold uppercase">{item.project}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded text-[9px] font-black uppercase border ${getStatusColor(item.status)}`}>
                  {item.status}
                </span>
              </div>

              <div className="flex items-center justify-between mb-3">
                {isApprover && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-xl">
                    <FiUser className="text-gray-400" size={12} />
                    <span className="text-[11px] font-bold text-gray-600">{item.name}</span>
                  </div>
                )}
                <div className="flex items-center gap-1 text-[9px] font-black text-gray-400 uppercase tracking-widest">
                  <FiCalendar size={10} />
                  Sent: {moment(item.submittedAt).format("DD/MM HH:mm")}
                </div>
              </div>

              <p className="text-xs text-gray-500 mb-4 line-clamp-2 italic">"{item.activity}"</p>

              <div className="flex items-center justify-between">
                <span className="text-lg font-black text-gray-900">
                  {item.entryHours} <small className="text-[10px] text-gray-400">HOURS</small>
                </span>
                <button
                  onClick={() => handleViewClick(item._id)}
                  className="px-5 py-2 bg-orange-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest"
                >
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-100 bg-white flex items-center justify-between mt-4 md:mt-0 rounded-2xl md:rounded-none">
          <div className="text-xs font-bold text-gray-400 uppercase tracking-widest">
            Page {currentPage} of {totalPages} &nbsp;·&nbsp; {filteredData.length} total
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className={`p-2 rounded-xl transition-colors ${
                currentPage === 1
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-600 hover:bg-orange-50 hover:text-orange-600"
              }`}
            >
              <FiChevronLeft size={18} />
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let page;
              if (totalPages <= 5) {
                page = i + 1;
              } else if (currentPage <= 3) {
                page = i + 1;
              } else if (currentPage >= totalPages - 2) {
                page = totalPages - 4 + i;
              } else {
                page = currentPage - 2 + i;
              }

              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-8 h-8 rounded-xl text-xs font-black transition-colors ${
                    currentPage === page
                      ? "bg-orange-600 text-white"
                      : "text-gray-500 hover:bg-orange-50 hover:text-orange-600"
                  }`}
                >
                  {page}
                </button>
              );
            })}

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className={`p-2 rounded-xl transition-colors ${
                currentPage === totalPages
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-600 hover:bg-orange-50 hover:text-orange-600"
              }`}
            >
              <FiChevronRight size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}